import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import QRCode from 'qrcode';
import { getMerchantByQr, approveMerchant } from '../actions/merchantActions';

const FRONTEND_BASE = window.location.origin || 'http://localhost:3000';

const statusConfig = {
  active:   { cls: 'bg-green-100 text-green-700 border border-green-200',  dot: 'bg-green-500' },
  pending:  { cls: 'bg-yellow-100 text-yellow-700 border border-yellow-200', dot: 'bg-yellow-500' },
  inactive: { cls: 'bg-gray-100 text-gray-500 border border-gray-200',     dot: 'bg-gray-400' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status || 'inactive'}
    </span>
  );
}

function Field({ label, value, mono, span }) {
  return (
    <div className={span ? 'sm:col-span-2 min-w-0' : 'min-w-0'}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-sm font-medium text-gray-800 break-words ${mono ? 'font-mono' : ''}`}>{value || <span className="text-gray-300">—</span>}</p>
    </div>
  );
}

export default function MerchantDetail() {
  const { qrToken } = useParams();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(s => s.userLogin || {});
  const { getRoleData } = useSelector(s => s.getRoleInfo || {});
  const { merchant, loading, error } = useSelector(s => s.merchantByQr);
  const activeRole = getRoleData || userInfo?.role || {};
  const canViewOnboardPage = !!activeRole.merchantPageView;
  const canEditMerchant = !!activeRole.merchantEdit;
  const isOwnMerchant = !!userInfo?.merchantId && userInfo?.merchantQrToken === qrToken;
  const displayedName = isOwnMerchant ? (userInfo?.name || merchant?.name) : merchant?.name;
  const displayedEmail = isOwnMerchant ? (userInfo?.email || merchant?.email) : merchant?.email;

  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [approving, setApproving] = useState(false);

  const formUrl = useMemo(() => `${FRONTEND_BASE}/form/${qrToken}`, [qrToken]);

  useEffect(() => { dispatch(getMerchantByQr(qrToken)); }, [dispatch, qrToken]);

  useEffect(() => {
    (async () => {
      try {
        const url = await QRCode.toDataURL(formUrl, { errorCorrectionLevel: 'M', margin: 2, width: 280 });
        setQrDataUrl(url);
      } catch { setQrDataUrl(''); }
    })();
  }, [formUrl]);

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(formUrl); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onApprove = async () => {
    setApproving(true);
    await dispatch(approveMerchant(merchant._id));
    dispatch(getMerchantByQr(qrToken));
    setApproving(false);
  };

  if (loading) {
    return (
      <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
        <div className="animate-pulse space-y-4 max-w-5xl">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-4 bg-gray-200 rounded w-64" />
          <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:gap-6">
            <div className="h-96 bg-gray-200 rounded-xl w-72 flex-shrink-0" />
            <div className="flex-1 h-96 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm max-w-lg">{String(error)}</div>
      </div>
    );
  }

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-6 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      {canViewOnboardPage && (
        <Link
          to="/admin/merchants/create"
          className="inline-flex items-center gap-2 mb-4 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft size={18} /> Back to Onboard Merchant
        </Link>
      )}

      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{displayedName || 'Merchant'}</h1>
          <StatusBadge status={merchant?.status} />
        </div>
        <p className="text-xs text-gray-400 font-mono break-all">Token: {qrToken}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 max-w-5xl">

        {/* ── QR Card ── */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 w-full lg:w-72 flex-shrink-0 overflow-hidden">
          {/* QR image */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4 sm:p-6 border-b border-gray-100 min-h-[220px] sm:min-h-[260px]">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR Code" className="rounded-lg shadow-sm w-44 h-44 sm:w-[200px] sm:h-[200px]" />
            ) : (
              <div className="text-gray-300 text-sm text-center">
                <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-2" />
                QR unavailable
              </div>
            )}
          </div>

          {/* URL + actions */}
          <div className="p-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1.5">Form URL</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <span className="text-xs font-mono text-gray-500 flex-1 truncate">{formUrl}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={onCopy}
                className={`text-xs font-semibold px-3 py-2.5 rounded-lg border transition-all ${
                  copied
                    ? 'border-green-400 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-600'
                }`}
              >
                {copied ? '✓ Copied' : 'Copy URL'}
              </button>
              {qrDataUrl && (
                <a
                  href={qrDataUrl}
                  download={`merchant-${displayedName || 'qr'}.png`}
                  className="text-xs font-semibold px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors text-center"
                >
                  Download
                </a>
              )}
            </div>

            <a
              href={formUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-1.5 w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors"
            >
              Open Form
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {merchant?._id && merchant?.status === 'pending' && canEditMerchant && (
              <button
                onClick={onApprove}
                disabled={approving}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center gap-1.5"
              >
                {approving ? (
                  <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Approving…</>
                ) : '✓ Approve Merchant'}
              </button>
            )}
          </div>
        </div>

        {/* ── Details Panel ── */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 flex-1 overflow-hidden min-w-0">
          {/* Panel header */}
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100 bg-gray-50/50">
            <h2 className="text-sm font-semibold text-gray-700">Merchant Details</h2>
          </div>

          <div className="p-4 sm:p-6">
            {/* Top row: Name + Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-5">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Name</p>
                <p className="text-base font-semibold text-gray-900 break-words">{displayedName || <span className="text-gray-300">—</span>}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Status</p>
                <div className="mt-0.5"><StatusBadge status={merchant?.status} /></div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              <Field label="QR Number" value={merchant?.qrNumber} mono />
              <Field label="Contact" value={merchant?.contactNumber} mono />
              <Field label="Email" value={displayedEmail} />
              <Field label="Location" value={merchant?.location} />
              <Field label="Pincode" value={merchant?.pincode} mono />
              <Field label="UPI ID" value={merchant?.upiId} mono />
              <Field label="Created" value={merchant?.createdAt ? new Date(merchant.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : undefined} />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
